// CreateWizard — US-125..US-130.
//
// Single scrollable page (matches the prototype) divided into sections:
//   1. Name (US-125).
//   2. Account + business (US-125).
//   3. Lead source (US-126/127).
//   4. Cadence (US-128).
//   5. Message sequence (US-129).
//   6. Submit footer (US-130).
//
// The whole form is local state — we POST to `/campaigns` only on submit
// (Save draft). The CSV upload, when used, completes BEFORE the create call
// so the resulting `csvImportId` is part of the create payload. CSV uploads
// pre-create-row use the special id `'draft'` for the presign endpoint.

import { useEffect, useMemo, useState } from 'react';

import {
  useBusinesses,
  useCreateCampaign,
  useGenerateCampaignSequence,
  useLinkedinAccounts,
  type CampaignSourceType,
  type CreateCampaignInput,
} from '@barel/api-client/react';
import { useLocale, useT } from '@barel/i18n';
import { PageHeader, toast } from '@barel/ui';

import { type MessageSequenceValue } from './MessageSequenceEditor';
import { canonicalizeCampaignPlaceholders, validateCampaignMessageSequence } from './lib/sequence';
import { normalizeCampaignSearchUrl } from './lib/sourceUrl';
import { type CSVImportResult } from './sources/CSVImportDropzone';
import { WizardAccountSection } from './wizard/WizardAccountSection';
import { WizardCadenceSection } from './wizard/WizardCadenceSection';
import { WizardFooter } from './wizard/WizardFooter';
import { WizardMessagesSection } from './wizard/WizardMessagesSection';
import { WizardNameSection } from './wizard/WizardNameSection';
import { WizardSourceSection } from './wizard/WizardSourceSection';

export interface CreateWizardProps {
  onCreated: (id: string) => void;
}

const DEFAULT_SEQUENCE: MessageSequenceValue = {
  invit: { enabled: true, delayDays: 0, messageText: '' },
  fu1: { enabled: true, delayDays: 3, messageText: '' },
  fu2: { enabled: true, delayDays: 7, messageText: '' },
  fu3: { enabled: false, delayDays: 14, messageText: '' },
};

export function CreateWizard({ onCreated }: CreateWizardProps) {
  const t = useT('campaigns');
  const locale = useLocale();
  const accountsQ = useLinkedinAccounts();
  const businessesQ = useBusinesses();
  const createMut = useCreateCampaign();
  const generateMut = useGenerateCampaignSequence();

  const accounts = useMemo(() => accountsQ.data?.accounts ?? [], [accountsQ.data?.accounts]);
  const allBusinesses = useMemo(
    () => businessesQ.data?.businesses ?? [],
    [businessesQ.data?.businesses],
  );

  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState<string>('');
  const [businessId, setBusinessId] = useState<string>('');
  const [sourceType, setSourceType] = useState<CampaignSourceType>('salesnav');
  const [sourceUrl, setSourceUrl] = useState<string>('');
  const [csvResult, setCsvResult] = useState<CSVImportResult | null>(null);

  const account = accounts.find((a) => a.id === accountId);
  const hasSalesNav = !!account?.salesNav;
  const businesses = useMemo(() => {
    if (!accountId) return [];
    const bound = allBusinesses.filter((business) => business.linkedinAccountId === accountId);
    const legacyUnbound = allBusinesses.filter((business) => business.linkedinAccountId === null);
    return [...bound, ...legacyUnbound];
  }, [accountId, allBusinesses]);
  const business = businesses.find((candidate) => candidate.id === businessId);
  const businessMatchesAccount = Boolean(business);

  // Pre-select the only available account so single-account users skip a click.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      setAccountId(accounts[0]?.id ?? '');
    }
  }, [accountId, accounts]);
  useEffect(() => {
    if (businessId && businesses.some((candidate) => candidate.id === businessId)) return;
    setBusinessId(businesses[0]?.id ?? '');
  }, [businessId, businesses]);

  // If user picks an account without SalesNav, fallback to linkedin.
  useEffect(() => {
    if (accountId && sourceType === 'salesnav' && !hasSalesNav) {
      setSourceType('linkedin');
      setSourceUrl('');
    }
  }, [accountId, sourceType, hasSalesNav]);

  const [dailyLimit, setDailyLimit] = useState<number>(15);

  const [sequence, setSequence] = useState<MessageSequenceValue>(DEFAULT_SEQUENCE);
  const [generationErrorCode, setGenerationErrorCode] = useState<string>();
  const [generatedExampleCount, setGeneratedExampleCount] = useState<number>();
  const sequenceValidation = useMemo(() => validateCampaignMessageSequence(sequence), [sequence]);
  const normalizedSourceUrl =
    sourceType === 'csv' ? null : normalizeCampaignSearchUrl(sourceUrl, sourceType);

  useEffect(() => {
    setGenerationErrorCode(undefined);
    setGeneratedExampleCount(undefined);
  }, [accountId, businessId]);

  const canCreate =
    name.trim().length > 0 &&
    accountId.length > 0 &&
    businessMatchesAccount &&
    sequenceValidation.valid &&
    (sourceType === 'csv' ? !!csvResult : normalizedSourceUrl !== null);

  const onAccountChange = (nextAccountId: string) => {
    if (nextAccountId === accountId) return;
    setAccountId(nextAccountId);
    setBusinessId('');
    setSourceUrl('');
  };

  const onSourceTypeChange = (nextSourceType: CampaignSourceType) => {
    if (nextSourceType === sourceType) return;
    setSourceType(nextSourceType);
    setSourceUrl('');
  };

  const onGenerateSequence = async () => {
    if (!accountId || !businessMatchesAccount) return;
    setGenerationErrorCode(undefined);
    setGeneratedExampleCount(undefined);
    try {
      const response = await generateMut.mutateAsync({
        linkedinAccountId: accountId,
        businessId,
        uiLang: locale === 'fr' ? 'fr' : 'en',
      });
      const generated = response.generated.steps;
      const next: MessageSequenceValue = {
        invit: {
          ...sequence.invit,
          messageText: canonicalizeCampaignPlaceholders(generated.invite),
        },
        fu1: { ...sequence.fu1, messageText: canonicalizeCampaignPlaceholders(generated.fu1) },
        fu2: { ...sequence.fu2, messageText: canonicalizeCampaignPlaceholders(generated.fu2) },
        fu3: { ...sequence.fu3, messageText: canonicalizeCampaignPlaceholders(generated.fu3) },
      };
      const hasOversizedStep = Object.values(next).some((step) => step.messageText.length > 300);
      if (hasOversizedStep || !validateCampaignMessageSequence(next).valid) {
        setGenerationErrorCode('ai.invalid_response');
        return;
      }
      setSequence(next);
      setGeneratedExampleCount(response.generated.evidence.userAuthoredExamples);
    } catch (error) {
      setGenerationErrorCode((error as { code?: string } | null)?.code ?? 'internal_error');
    }
  };

  const onSubmit = async () => {
    if (!canCreate) return;
    const stepsPayload: NonNullable<CreateCampaignInput['steps']> = [
      { stepKind: 'invite', enabled: true, delayDays: 0, messageText: sequence.invit.messageText },
      {
        stepKind: 'fu1',
        enabled: sequence.fu1.enabled,
        delayDays: sequence.fu1.delayDays,
        messageText: sequence.fu1.messageText,
      },
      {
        stepKind: 'fu2',
        enabled: sequence.fu2.enabled,
        delayDays: sequence.fu2.delayDays,
        messageText: sequence.fu2.messageText,
      },
      {
        stepKind: 'fu3',
        enabled: sequence.fu3.enabled,
        delayDays: sequence.fu3.delayDays,
        messageText: sequence.fu3.messageText,
      },
    ];
    try {
      const created = await createMut.mutateAsync({
        name: name.trim(),
        linkedinAccountId: accountId,
        businessId,
        sourceType,
        sourceUrl: sourceType === 'csv' ? undefined : (normalizedSourceUrl ?? undefined),
        csvImportId: sourceType === 'csv' ? csvResult?.csvImportId : undefined,
        dailyLimit,
        invitHasMessage: sequence.invit.messageText.trim().length > 0,
        steps: stepsPayload,
      });
      // SPEC-CAMPAIGN-LOOP §6 — the create-time CSV parse (§3.1) reports
      // its outcome on the 201. A failed parse (or a parse that seeded 0
      // runs) means the campaign has nobody to contact — surface it loud
      // instead of the success toast. The draft still persists (US-130);
      // launch is hard-blocked by `campaign.no_leads`.
      const csvBroken =
        sourceType === 'csv' &&
        (created.csvParse?.status === 'errored' || created.csvParse?.seededRuns === 0);
      if (csvBroken) {
        toast.error({ title: t('create.csv.errorParse') });
      } else {
        toast.success({ title: t('toast.created') });
      }
      onCreated(created.campaign.id);
    } catch {
      toast.error({ title: t('toast.error') });
    }
  };

  return (
    <section className="flex flex-col gap-6 max-w-3xl" data-testid="campaign-wizard">
      <PageHeader title={t('create.title')} subtitle={t('create.subtitle')} />

      {/* Section 1 — name */}
      <WizardNameSection name={name} onNameChange={setName} />

      {/* Section 2 — account + business */}
      <WizardAccountSection
        accounts={accounts}
        businesses={businesses}
        accountId={accountId}
        onAccountChange={onAccountChange}
        businessId={businessId}
        onBusinessChange={setBusinessId}
        hasSalesNav={hasSalesNav}
      />

      {/* Section 3 — source */}
      <WizardSourceSection
        sourceType={sourceType}
        onSourceTypeChange={onSourceTypeChange}
        hasSalesNav={hasSalesNav}
        accountName={account?.name ?? ''}
        sourceUrl={sourceUrl}
        onSourceUrlChange={setSourceUrl}
        csvResult={csvResult}
        onCsvResultChange={setCsvResult}
      />

      {/* Section 4 — cadence */}
      <WizardCadenceSection dailyLimit={dailyLimit} onDailyLimitChange={setDailyLimit} />

      {/* Section 5 — messages */}
      <WizardMessagesSection
        sequence={sequence}
        validation={sequenceValidation}
        onSequenceChange={setSequence}
        onGenerate={onGenerateSequence}
        canGenerate={Boolean(accountId && businessMatchesAccount)}
        isGenerating={generateMut.isPending}
        generationErrorCode={generationErrorCode}
        generatedExampleCount={generatedExampleCount}
      />

      {/* Footer CTAs */}
      <WizardFooter onSubmit={onSubmit} canCreate={canCreate} isPending={createMut.isPending} />
    </section>
  );
}
